import { CodedProperty, NamedProperty } from "sprint-common-web";

import { ContentAssetsListHrefContent } from "../../../assets/AssetsRoutes";
import { AssetOperationResponseItem } from "../../../common/api/master-api/assets/asset-operations/GetAssetsOperationsResponse";
import { AncillaryContentSubtype, isAncillaryContentSubtype } from "../../../common/types/AncillaryContentType";
import {
    ContentDetails as ContentDetailsResponse,
    isAncillaryContentDetails,
    isMovieContentDetails,
    isTvSeriesContentDetails
} from "../../../common/types/ContentDetails";
import { ContentReferenceType } from "../../../common/types/ContentReferenceType";
import { isMovieContentSubtype, MovieContentSubtype } from "../../../common/types/MovieContentSubtype";
import { RecordType } from "../../../common/types/RecordType";
import { isTvSeriesContentSubtype, TvSeriesContentSubtype } from "../../../common/types/TvSeriesContentSubtype";
import { VersionSummary } from "../../../common/types/VersionSummary";
import {
    renderBrandedContentVersionText,
    renderContentVersionText
} from "../../../common/utils/renderContentVersionText";

export type AssetOperationListItem = VideoAssetOperationItem | AudioAssetOperationItem | SubtitleAssetOperationItem;

export interface AssetOperationItemBase {
    id: string;
    operationType: number;
    content: ContentDetails;
}

export type ContentDetails = MovieDetails | TvSeriesDetails | AncillaryDetails;

interface MovieDetails {
    contentType: ContentReferenceType.Movie;
    contentTitle: string;
    contentVersions: string[];
    subtype: MovieContentSubtype;
    movie: NamedProperty;
    versionSummary: VersionSummary;
}

interface TvSeriesDetails {
    contentType: ContentReferenceType.TvSeries;
    contentTitle: string;
    contentVersions: string[];
    subtype: TvSeriesContentSubtype;
    tvSeries: NamedProperty;
    season: NamedProperty;
    episode: NamedProperty;
    versionSummary: VersionSummary;
}

interface AncillaryDetails {
    contentType: ContentReferenceType.Ancillary;
    contentTitle: string;
    contentVersions: string[];
    subtype: AncillaryContentSubtype;
    ancillary: NamedProperty;
    airline: CodedProperty;
}

interface VideoAssetOperationItem extends AssetOperationItemBase {
    recordType: RecordType.Video;
    uniquenessKey: { aspectRatio: NamedProperty; aspectType?: NamedProperty; subtitleLanguage: CodedProperty };
}

interface AudioAssetOperationItem extends AssetOperationItemBase {
    recordType: RecordType.Audio;
    uniquenessKey: { soundtrackLanguage: CodedProperty };
}

interface SubtitleAssetOperationItem extends AssetOperationItemBase {
    recordType: RecordType.Subtitle;
    uniquenessKey: { subtitleLanguage: CodedProperty; subtitleType: number };
}

export const mapToAssetOperationListItem = (response: AssetOperationResponseItem): AssetOperationListItem => {
    return {
        id: response.id,
        operationType: response.operationType,
        recordType: response.recordType,
        uniquenessKey: response.uniquenessKey,
        content: {
            ...response.content,
            contentType: mapToContentType(response.content),
            contentTitle: mapToContentTitle(response.content),
            contentVersions: mapToContentVersions(response.content),
            subtype: response.content.subtype
        }
    } as AssetOperationListItem;
};

const mapToContentType = (content: ContentDetailsResponse): ContentReferenceType => {
    if (isMovieContentSubtype(content.subtype)) {
        return ContentReferenceType.Movie;
    }
    if (isTvSeriesContentSubtype(content.subtype)) {
        return ContentReferenceType.TvSeries;
    }
    if (isAncillaryContentSubtype(content.subtype)) {
        return ContentReferenceType.Ancillary;
    }
    throw new Error(`Unknown content subtype: ${content.subtype}`);
};

export const mapToContentTitle = (content: ContentDetailsResponse): string => {
    if (isMovieContentDetails(content)) {
        return content.movie.name;
    }
    if (isTvSeriesContentDetails(content)) {
        return content.tvSeries.name;
    }
    if (isAncillaryContentDetails(content)) {
        return content.ancillary.name;
    }
    throw new Error("Unknown content type");
};

export const mapToContentVersions = (content: ContentDetailsResponse): string[] => {
    if (isMovieContentDetails(content) || isTvSeriesContentDetails(content)) {
        return content.versions.map(renderBrandedContentVersionText);
    }

    if (isAncillaryContentDetails(content)) {
        return [
            renderContentVersionText({
                distributor: content.versionSummary.distributors[0],
                type: content.versionSummary.versionType,
                airlines: [content.airline]
            })
        ];
    }

    throw new Error("Unknown content type");
};

export const mapToContentAssetsListHrefContent = (content: ContentDetails): ContentAssetsListHrefContent => {
    return { ...content, type: content.contentType } as ContentAssetsListHrefContent;
};
